import React, { useEffect, useRef, useState, useMemo } from "react";
import { Icon } from "@amzn/awsui-components-react";
import ConsoleIcon from "@amzn/awsui-components-console/icon";
import useKBAutosuggest from "src/hooks/useKBAutosuggest";
import "src/Ateam-BI/AutoMate/components/AISearchBar/AISearchBar.scss";

const MAX_SUGGESTIONS = 20;
const MAX_DESC_LEN = 80;

// Truncate a description to a uniform length so local suggestions (which carry
// the full app description) look the same as the shorter OpenSearch excerpts.
const trimDesc = (text) => {
  if (!text) return "";
  const t = String(text).trim();
  return t.length > MAX_DESC_LEN ? `${t.slice(0, MAX_DESC_LEN).trimEnd()}…` : t;
};

/**
 * OpenSearch-backed search bar for the Apps Library.
 *
 * Renders the AutoMate-style pill input with live typeahead suggestions
 * (powered by the same autosuggest AppSync API as the AI search). It is a
 * drop-in replacement for the old Polaris <Autosuggest>: on submit (Enter,
 * the Search button, or picking a suggestion) it calls onSubmit(value) so the
 * parent can drive the existing client-side filtering via the searchQuery
 * param. The grid is intentionally NOT refreshed while typing.
 *
 * `localApps` is the page's already-loaded app list ([{ value, description }]).
 * We filter it synchronously on every keystroke and show those matches
 * instantly, then merge in the OpenSearch suggestions once they arrive — so
 * the dropdown is never empty while the ~1-2s OpenSearch call is in flight.
 */
function AppOpenSearchBar({ initialValue = "", placeholder = "Search all applications", onSubmit, localApps = [] }) {
  const [query, setQuery] = useState(initialValue);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);

  const {
    suggestions,
    isLoading: suggestionsLoading,
    onQueryChange: onSuggestQueryChange,
  } = useKBAutosuggest();

  // Instant client-side matches from the apps already loaded on the page.
  // Cheap substring filter on name + description, recomputed per keystroke.
  const localSuggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const out = [];
    for (const app of localApps) {
      const name = (app?.value || "").toLowerCase();
      const desc = (app?.description || "").toLowerCase();
      if (name.includes(q) || desc.includes(q)) {
        out.push(app);
        if (out.length >= MAX_SUGGESTIONS) break;
      }
    }
    return out;
  }, [query, localApps]);

  // Merge: OpenSearch results first (more relevant), then any local matches
  // not already present, deduped by lowercased value. While OpenSearch is
  // loading (suggestions empty) this is purely the instant local list.
  const mergedSuggestions = useMemo(() => {
    const merged = [];
    const seen = new Set();
    for (const s of [...suggestions, ...localSuggestions]) {
      const key = (s?.value || "").toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(s);
      if (merged.length >= MAX_SUGGESTIONS) break;
    }
    return merged;
  }, [suggestions, localSuggestions]);

  // Keep the input in sync if the param changes elsewhere (e.g. back button).
  useEffect(() => {
    setQuery(initialValue);
  }, [initialValue]);

  // Close the suggestions dropdown on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleChange = (value) => {
    setQuery(value);
    setShowSuggestions(value.trim().length > 0);
    onSuggestQueryChange(value);
  };

  const submit = (value) => {
    setShowSuggestions(false);
    if (onSubmit) onSubmit((value ?? query).trim());
  };

  const handleSuggestionClick = (suggestion) => {
    const value = suggestion?.value || "";
    setQuery(value);
    submit(value);
  };

  return (
    <div className="ai-search-container">
      <div className="ai-search-input-row">
        <div className="ai-search-wrapper" ref={wrapperRef}>
          <div className="ai-icon">
            <ConsoleIcon name="gen-ai" />
          </div>
          <input
            type="text"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showSuggestions && mergedSuggestions.length > 0}
            aria-haspopup="listbox"
            value={query}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
              if (e.key === "Escape") setShowSuggestions(false);
            }}
            onFocus={() => {
              if (query.trim().length > 0 && mergedSuggestions.length > 0) setShowSuggestions(true);
            }}
            placeholder={placeholder}
            className="ai-search-input"
          />
          {showSuggestions && (mergedSuggestions.length > 0 || suggestionsLoading) && (
            <div className="ai-suggestions-dropdown" role="listbox">
              {mergedSuggestions.length === 0 && suggestionsLoading ? (
                <div className="ai-suggestion-item" aria-disabled="true">
                  <span className="ai-suggestion-icon"><Icon name="status-in-progress" /></span>
                  Searching…
                </div>
              ) : (
                mergedSuggestions.map((s, index) => (
                  <div
                    key={`${s.value}-${index}`}
                    role="option"
                    aria-selected="false"
                    className="ai-suggestion-item"
                    onMouseDown={() => handleSuggestionClick(s)}
                  >
                    <span className="ai-suggestion-icon"><Icon name="search" /></span>
                    <span>
                      <strong>{s.value}</strong>
                      {s.description ? <span style={{ color: "#777" }}> — {trimDesc(s.description)}</span> : null}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        <div className="ai-button-container">
          <button onClick={() => submit()} className="ai-ask-button">
            Search
          </button>
        </div>
      </div>
    </div>
  );
}

export default AppOpenSearchBar;
